/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { ProgrameeTyping } from '@/typings/widget';
import { useI18n } from 'vue-i18n';

export const formatCurrency = (
    cents: number, currency: string | undefined = 'EUR', locale = 'NL',
): string => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
})
    .format(cents / 100);

export const formatCurrencyNoCent = (
    cents: number, currency: string | undefined = 'EUR', locale = 'NL',
): string => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0, // No cents
    maximumFractionDigits: 0, // No cents
})
    .format(cents);

export const formatNumber = (number: number, options = {}): string => {
    const { locale } = useI18n();

    return new Intl.NumberFormat(locale.value, options)
        .format(number);
};

/**
 * Calculates the total duration in minutes across all days/programmes.
 * For each day/programme, sums the duration of each individual timeslot
 * (from its startDateTime to its endDateTime).
 *
 * @param days - Array of days/programs containing timeslots
 * @returns Total duration in minutes, or 0 if no valid timeslots exist
 */
export const calculateTotalMinutes = (days: ProgrameeTyping[]): number => {
    if (!days?.length) return 0;

    let totalMinutes = 0;

    for (const day of days) {
        const slots = day.timeslots;

        if (slots?.length) {
            for (const timeslot of slots) {
                const { startDateTime, endDateTime } = timeslot;

                if (startDateTime && endDateTime) {
                    const startDate = new Date(String(startDateTime));
                    const endDate = new Date(String(endDateTime));

                    const diffInMs = Math.abs(endDate.getTime() - startDate.getTime());

                    totalMinutes += diffInMs / (1000 * 60);
                }
            }
        }
    }

    return totalMinutes;
};

/**
 * Formats total minutes from days/programmes into a human-readable hours string.
 * Uses the default translation key prefix 'localize'.
 *
 * @param days - Array of days/programs containing timeslots
 * @param translationPrefix - Optional prefix for translation keys (default: 'localize')
 * @returns Formatted string like "2h" or "2h 30m"
 */
export const totalHours = (days: ProgrameeTyping[],
    translationPrefix = 'localize'): string => {
    const { t } = useI18n();
    const minutes = calculateTotalMinutes(days);

    if (minutes % 60 === 0) {
        return t(`${translationPrefix}.hours.short`, { hours: formatNumber(minutes / 60, { maximumSignificantDigits: 1 }) });
    }

    return t(`${translationPrefix}.hoursAndMinutes.short`, {
        hours: formatNumber(Math.floor(minutes / 60), { maximumSignificantDigits: 1 }),
        minutes: formatNumber(Math.floor(minutes % 60), { maximumSignificantDigits: 1 }),
    });
};

export const formatTime = (time: string): string => {
    const date = new Date(time);
    const hours = date.getHours()
        .toString()
        .padStart(2, '0');
    const minutes = date.getMinutes()
        .toString()
        .padStart(2, '0');
    return `${hours}:${minutes}`;
};
